import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import { useAuth0 } from '@auth0/auth0-vue';
import { isEmpty } from 'lodash';
import { ROUTER } from '../app-constants/router.constant';

import { useAuthStore } from '../stores/auth.store';
import { LAYOUTS } from '../app-constants/layout.constant';
import { useAppStore } from '../stores/app.store';
import { useImpersonationInfo } from '@/hooks/useImpersonationInfo';
import { validateNextRoute } from '@/utils/router.utils';
import { isAdministrator, isCustomerSuccessManager } from '@/utils/user.utils';
import { authUserStorage } from '@/storages/browser.storage';
import { usePartnerTenantStore } from '@/stores/partnerTenant.storage';
import { useDashboardReportStore } from '@/stores/dashboardReports.store';
import { trackEvent, TrackingEventType } from '@/services/tracking.service';
import { useModuleStore } from '@/stores/module.store';
import type { AccessPolicy } from '@/entities/role.entity';
import type { RouteLocationNormalized } from 'vue-router';

const defaultRoutes: RouteRecordRaw[] = [
  /*
   * Routes for Customer
   */
  {
    name: ROUTER.CUSTOMER.HOME_PAGE.KEY,
    path: ROUTER.CUSTOMER.HOME_PAGE.PATH,
    component: () => import('@/views/customer-home/CustomerHomeView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.HOME_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.HOME_PAGE.accessPolicy,
    },
  },
  {
    path: ROUTER.CUSTOMER.DASHBOARDS_PAGE.PATH,
    redirect: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.PATH, // Redirect to Dashboard - Google SecOps SIEM
    meta: {
      layout: LAYOUTS.CUSTOMER.name,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.KEY, // Google SecOps SIEM
    path: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-siem/CustomerDashboardSIEMView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_SIEM_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.KEY, // Microsoft Sentinel SIEM
    path: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-azure-sentinel/CustomerDashboardAzureSentinelView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.KEY, // MITRE ATT&CK Coverage
    path: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-mitre/CustomerDashboardMITREView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_MITRE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.KEY, // SentinelOne EDR
    path: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-edr/CustomerDashboardEDRView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_EDR_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.KEY,
    path: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-edr-crowdstrike/CustomerDashboardEDRCrowdStrikeView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.KEY, // Case Insights
    path: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-cases/CustomerDashboardCasesView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_CASE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.KEY, // Fortra Vulnerability Management
    path: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-vulnerability/CustomerDashboardVulnerabilityView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY,
    path: ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-CISO/CustomerDashboardCISOView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_CISO_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.KEY,
    path: ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-fortianalyzer/CustomerDashboardFortiAnalyzerView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_FORTIANALYZER_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.KEY,
    path: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-patch-management/CustomerDashboardPatchManagementView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY,
    path: ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-prompt-ai/CustomerDashboardPromptAIView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.FILES_PAGE.KEY,
    path: ROUTER.CUSTOMER.FILES_PAGE.PATH,
    component: () => import('@/views/customer-files/CustomerFilesView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.FILES_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.FILES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.ACTIVITIES_PAGE.KEY,
    path: ROUTER.CUSTOMER.ACTIVITIES_PAGE.PATH,
    component: () => import('@/views/customer-activity/CustomerActivityView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.ACTIVITIES_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.ACTIVITIES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.MY_LINKS_PAGE.KEY,
    path: ROUTER.CUSTOMER.MY_LINKS_PAGE.PATH,
    component: () => import('@/views/customer-my-links/CustomerMyLinksView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.MY_LINKS_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.MY_LINKS_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.SECRET_MANAGEMENT_PAGE.KEY,
    path: ROUTER.CUSTOMER.SECRET_MANAGEMENT_PAGE.PATH,
    component: () => import('@/views/customer-secret-management/CustomerSecretManagementView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.SECRET_MANAGEMENT_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.SECRET_MANAGEMENT_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.SERVICE_PAGE.KEY,
    path: ROUTER.CUSTOMER.SERVICE_PAGE.PATH,
    component: () => import('@/views/customer-services/CustomerServicesView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.SERVICE_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.SERVICE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.SERVICENOW_DETAIL_PAGE.KEY,
    path: ROUTER.CUSTOMER.SERVICENOW_DETAIL_PAGE.PATH,
    component: () => import('@/views/customer-securitynow-tickets/CustomerServiceNowDetail.vue'),
    meta: {
      title: ROUTER.CUSTOMER.SERVICENOW_DETAIL_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      shouldScrollToTop: true,
      accessPolicy: ROUTER.CUSTOMER.SERVICENOW_DETAIL_PAGE.accessPolicy,
    },
    props: true,
  },
  {
    name: ROUTER.CUSTOMER.NOTIFICATION_PAGE.KEY,
    path: ROUTER.CUSTOMER.NOTIFICATION_PAGE.PATH,
    component: () => import('@/views/customer-notifications/CustomerNotificationsView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.NOTIFICATION_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.NOTIFICATION_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.CONTACT_US_PAGE.KEY,
    path: ROUTER.CUSTOMER.CONTACT_US_PAGE.PATH,
    component: () => import('@/views/customer-contact-us/CustomerContactUsView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.CONTACT_US_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.CONTACT_US_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.KEY,
    path: ROUTER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.PATH,
    component: () => import('@/views/customer-security-survey/CustomerSecuritySurveyView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.accessPolicy,
      // force hide tenant name in the header
      showTenantName: false,
    },
  },
  {
    name: ROUTER.CUSTOMER.MY_CASES_PAGE.KEY,
    path: ROUTER.CUSTOMER.MY_CASES_PAGE.PATH,
    component: () => import('@/views/customer-securitynow-tickets/CustomerServiceNowTicketsView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.MY_CASES_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.MY_CASES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.CUSTOMER.USER_MANAGEMENT_PAGE.KEY,
    path: ROUTER.CUSTOMER.USER_MANAGEMENT_PAGE.PATH,
    component: () => import('@//views/customer-user-management/CustomerUserManagementView.vue'),
    meta: {
      title: ROUTER.CUSTOMER.USER_MANAGEMENT_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.CUSTOMER.USER_MANAGEMENT_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.REPORT_SCHEDULES_PAGE.KEY,
    path: ROUTER.REPORT_SCHEDULES_PAGE.PATH,
    component: () => import('@/views/customer-report-schedules/CustomerReportSchedulesView.vue'),
    meta: {
      title: ROUTER.REPORT_SCHEDULES_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.REPORT_SCHEDULES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.REPORT_HISTORY_PAGE.KEY,
    path: ROUTER.REPORT_HISTORY_PAGE.PATH,
    component: () => import('@/views/customer-report-history/CustomerReportHistoryView.vue'),
    meta: {
      title: ROUTER.REPORT_HISTORY_PAGE.TITLE,
      layout: LAYOUTS.CUSTOMER.name,
      accessPolicy: ROUTER.REPORT_HISTORY_PAGE.accessPolicy,
    },
  },
  /*
   * Routes for Admin
   */
  {
    name: ROUTER.ADMIN.HOME_PAGE.KEY,
    path: ROUTER.ADMIN.HOME_PAGE.PATH,
    component: () => import('@/views/admin-home/AdminHomeView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN.name,
      title: ROUTER.ADMIN.HOME_PAGE.TITLE,
      accessPolicy: ROUTER.ADMIN.HOME_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN.TENANTS_PAGE.KEY,
    path: ROUTER.ADMIN.TENANTS_PAGE.PATH,
    component: () => import('@/views/admin-home/AdminHomeView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN.name,
      title: ROUTER.ADMIN.TENANTS_PAGE.TITLE,
      accessPolicy: ROUTER.ADMIN_TENANT.HOME_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN.FILES_PAGE.KEY,
    path: ROUTER.ADMIN.FILES_PAGE.PATH,
    component: () => import('@/views/admin-files/AdminFilesView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN.name,
      title: ROUTER.ADMIN.FILES_PAGE.TITLE,
      accessPolicy: ROUTER.ADMIN.FILES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN.CSM_PAGE.KEY,
    path: ROUTER.ADMIN.CSM_PAGE.PATH,
    component: () => import('@/views/admin-csm/AdminCSMView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN.name,
      title: ROUTER.ADMIN.CSM_PAGE.TITLE,
      accessPolicy: ROUTER.ADMIN.CSM_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN.CSM_MANAGED_CUSTOMERS_PAGE.KEY,
    path: ROUTER.ADMIN.CSM_MANAGED_CUSTOMERS_PAGE.PATH,
    component: () => import('@/views/admin-csm/AdminCSMManagedCustomersView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN.name,
      title: ROUTER.ADMIN.CSM_MANAGED_CUSTOMERS_PAGE.TITLE,
      accessPolicy: ROUTER.ADMIN.CSM_PAGE.accessPolicy,
    },
  },
  /*
   * Routes for Admin Tenant
   */
  {
    name: ROUTER.ADMIN_TENANT.HOME_PAGE.KEY,
    path: ROUTER.ADMIN_TENANT.HOME_PAGE.PATH,
    component: () => import('@/views/admin-tenant/AdminTenantHomeView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN_TENANT.name,
      title: ROUTER.ADMIN_TENANT.HOME_PAGE.TITLE,
      dynamicTitle: ROUTER.ADMIN_TENANT.HOME_PAGE.DYNAMIC_TITLE,
      sourcePath: ROUTER.ADMIN_TENANT.HOME_PAGE.PATH,
      accessPolicy: ROUTER.ADMIN_TENANT.HOME_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN_TENANT.LOGS_PAGE.KEY,
    path: ROUTER.ADMIN_TENANT.LOGS_PAGE.PATH,
    component: () => import('@/views/admin-tenant/AdminTenantLogsView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN_TENANT.name,
      title: ROUTER.ADMIN_TENANT.LOGS_PAGE.TITLE,
      dynamicTitle: ROUTER.ADMIN_TENANT.LOGS_PAGE.DYNAMIC_TITLE,
      sourcePath: ROUTER.ADMIN_TENANT.LOGS_PAGE.PATH,
      accessPolicy: ROUTER.ADMIN_TENANT.LOGS_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN_TENANT.FILES_PAGE.KEY,
    path: ROUTER.ADMIN_TENANT.FILES_PAGE.PATH,
    component: () => import('@/views/admin-tenant/AdminTenantFilesView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN_TENANT.name,
      title: ROUTER.ADMIN_TENANT.FILES_PAGE.TITLE,
      dynamicTitle: ROUTER.ADMIN_TENANT.FILES_PAGE.DYNAMIC_TITLE,
      sourcePath: ROUTER.ADMIN_TENANT.FILES_PAGE.PATH,
      accessPolicy: ROUTER.ADMIN_TENANT.FILES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.ADMIN_TENANT.SECURITY_POSTURE_PAGE.KEY,
    path: ROUTER.ADMIN_TENANT.SECURITY_POSTURE_PAGE.PATH,
    component: () => import('@/views/admin-tenant/AdminTenantSecurityPostureView.vue'),
    meta: {
      layout: LAYOUTS.ADMIN_TENANT.name,
      title: ROUTER.ADMIN_TENANT.SECURITY_POSTURE_PAGE.TITLE,
      dynamicTitle: ROUTER.ADMIN_TENANT.SECURITY_POSTURE_PAGE.DYNAMIC_TITLE,
      sourcePath: ROUTER.ADMIN_TENANT.SECURITY_POSTURE_PAGE.PATH,
      accessPolicy: ROUTER.ADMIN_TENANT.SECURITY_POSTURE_PAGE.accessPolicy,
    },
  },

  // Auth Router
  {
    name: ROUTER.AUTH_CALLBACK.KEY,
    path: ROUTER.AUTH_CALLBACK.PATH,
    component: () => import('@/views/auth/AuthCallBackView.vue'),
    meta: {
      isPublic: true,
      layout: LAYOUTS.SIGNUP.name,
    },
  },
  {
    name: ROUTER.AUTH_SIGNUP.KEY,
    path: ROUTER.AUTH_SIGNUP.PATH,
    component: () => import('@/views/auth/SignupView.vue'),
    meta: {
      isPublic: true,
      layout: LAYOUTS.SIGNUP.name,
    },
  },
  {
    name: ROUTER.AUTH_EMAIL_VERIFY.KEY,
    path: ROUTER.AUTH_EMAIL_VERIFY.PATH,
    component: () => import('@/views/auth/EmailVerifyView.vue'),
    meta: {
      isPublic: true,
      layout: LAYOUTS.SIGNUP.name,
    },
  },
  {
    name: ROUTER.AUTH_ACCOUNT_INACTIVATED.KEY,
    path: ROUTER.AUTH_ACCOUNT_INACTIVATED.PATH,
    component: () => import('@/views/auth/AuthFailView.vue'),
    meta: {
      isPublic: true,
      layout: LAYOUTS.SIGNUP.name,
    },
  },

  /*
   * Routes for Partner
   */
  {
    name: ROUTER.PARTNER.LANDING_PAGE.KEY,
    path: ROUTER.PARTNER.LANDING_PAGE.PATH,
    component: () => import('@/views/partner-landing-page/PartnerHomeView.vue'),
    meta: {
      layout: LAYOUTS.PARTNER.name,
      title: ROUTER.PARTNER.LANDING_PAGE.TITLE,
      accessPolicy: ROUTER.PARTNER.LANDING_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.HOME_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.HOME_PAGE.PATH,
    component: () => import('@/views/customer-home/CustomerHomeView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.HOME_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.CUSTOMER.HOME_PAGE.accessPolicy,
    },
  },
  {
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PAGE.PATH,
    redirect: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.PATH, // Redirect to Dashboard - Google SecOps SIEM
    meta: {
      layout: LAYOUTS.PARTNER_TENANT.name,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.KEY, // Google SecOps SIEM
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-siem/CustomerDashboardSIEMView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_SIEM_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.KEY, // Microsoft Sentinel SIEM
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-azure-sentinel/CustomerDashboardAzureSentinelView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_AZURE_SENTINEL_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.KEY, // MITRE ATT&CK Coverage
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-mitre/CustomerDashboardMITREView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_MITRE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.KEY, // SentinelOne EDR
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-edr/CustomerDashboardEDRView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-edr-crowdstrike/CustomerDashboardEDRCrowdStrikeView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_EDR_CROWDSTRIKE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.KEY, // Case Insights
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-cases/CustomerDashboardCasesView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CASE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.KEY, // Fortra Vulnerability Management
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-vulnerability/CustomerDashboardVulnerabilityView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_VULNERABILITY_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-CISO/CustomerDashboardCISOView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.KEY,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_CISO_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-patch-management/CustomerDashboardPatchManagementView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PATCH_MANAGEMENT_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.PATH,
    component: () => import('@/views/customer-dashboard-prompt-ai/CustomerDashboardPromptAIView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.DASHBOARDS_PROMPT_AI_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.FILES_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.FILES_PAGE.PATH,
    component: () => import('@/views/customer-files/CustomerFilesView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.FILES_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.FILES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.ACTIVITIES_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.ACTIVITIES_PAGE.PATH,
    component: () => import('@/views/customer-activity/CustomerActivityView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.ACTIVITIES_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.ACTIVITIES_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.MY_LINKS_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.MY_LINKS_PAGE.PATH,
    component: () => import('@/views/customer-my-links/CustomerMyLinksView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.MY_LINKS_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.MY_LINKS_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.SERVICE_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.SERVICE_PAGE.PATH,
    component: () => import('@/views/customer-services/CustomerServicesView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.SERVICE_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.SERVICE_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.SERVICENOW_DETAIL_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.SERVICENOW_DETAIL_PAGE.PATH,
    component: () => import('@/views/customer-securitynow-tickets/CustomerServiceNowDetail.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.SERVICENOW_DETAIL_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      shouldScrollToTop: true,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.SERVICENOW_DETAIL_PAGE.accessPolicy,
    },
    props: true,
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.CONTACT_US_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.CONTACT_US_PAGE.PATH,
    component: () => import('@/views/customer-contact-us/CustomerContactUsView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.CONTACT_US_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.CONTACT_US_PAGE.accessPolicy,
      showTenantName: true,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.PATH,
    component: () => import('@/views/customer-security-survey/CustomerSecuritySurveyView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.SECURITY_POSTURE_SURVEY_TENANT_PAGE.accessPolicy,
    },
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.MY_CASES_PAGE.KEY, //My Cases
    path: ROUTER.PARTNER.CUSTOMER.MY_CASES_PAGE.PATH,
    component: () => import('@/views/customer-securitynow-tickets/CustomerServiceNowTicketsView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.MY_CASES_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      shouldScrollToTop: true,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.MY_CASES_PAGE.accessPolicy,
    },
    props: true,
  },
  {
    name: ROUTER.PARTNER.CUSTOMER.USER_MANAGEMENT_PAGE.KEY,
    path: ROUTER.PARTNER.CUSTOMER.USER_MANAGEMENT_PAGE.PATH,
    component: () => import('@//views/customer-user-management/CustomerUserManagementView.vue'),
    meta: {
      title: ROUTER.PARTNER.CUSTOMER.USER_MANAGEMENT_PAGE.TITLE,
      layout: LAYOUTS.PARTNER_TENANT.name,
      accessPolicy: ROUTER.PARTNER.CUSTOMER.USER_MANAGEMENT_PAGE.accessPolicy,
    },
  },
  // Report render route — bypasses Auth0, used by headless Chrome for PDF capture
  {
    name: ROUTER.REPORT_RENDER.KEY,
    path: ROUTER.REPORT_RENDER.PATH,
    component: () => import('@/views/report-render/ReportRenderView.vue'),
    meta: { layout: 'none', requiresAuth: false },
  },
  // redirect to home page if route not found
  {
    path: '/:pathMatch(.*)*',
    redirect: (): object => {
      const authUser = authUserStorage.get();
      if (!authUser) {
        return { path: ROUTER.CUSTOMER.HOME_PAGE.PATH };
      }
      if (isAdministrator(authUser) || isCustomerSuccessManager(authUser)) {
        return { path: ROUTER.ADMIN.HOME_PAGE.PATH };
      }
      return { path: ROUTER.CUSTOMER.HOME_PAGE.PATH };
    },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: defaultRoutes,
});

router.beforeEach(async (to) => {
  const { incrementAppBarLoadingCount } = useAppStore();
  const { tenant: partnerSelectedTenant } = usePartnerTenantStore();
  const { loginWithRedirect } = useAuth0();
  const { getUser } = useAuthStore();
  const { isImpersonating, checkIfAuthUserCanImpersonate, stopImpersonating, impersonatedUser } =
    useImpersonationInfo();
  const dashboardReportStore = useDashboardReportStore();

  const authUser = getUser();

  if (!to.meta.isPublic && to.meta.requiresAuth !== false && isEmpty(authUser)) {
    loginWithRedirect();
    return false;
  }

  if (isImpersonating && !checkIfAuthUserCanImpersonate(authUser)) {
    stopImpersonating();
    dashboardReportStore.resetDefault();
  }

  // if user is impersonating, check if the user can access the route
  const nextRoute = await validateNextRoute(to, isImpersonating ? impersonatedUser : authUser, partnerSelectedTenant);
  if (nextRoute === false || (typeof nextRoute === 'string' && nextRoute !== to.path)) {
    return nextRoute;
  }
  incrementAppBarLoadingCount();
});

const resolveModuleId = (route: RouteLocationNormalized): string | null => {
  const accessPolicy = route.meta?.accessPolicy as AccessPolicy | undefined;
  if (!accessPolicy?.moduleName) return null;
  return useModuleStore().getModuleIdByName(accessPolicy.moduleName);
};

const trackPageView = (to: RouteLocationNormalized, from: RouteLocationNormalized): void => {
  const pagePath = to.fullPath;
  const referrerPath = from.name ? from.fullPath : undefined;
  const queryParams = Object.keys(to.query).length > 0 ? (to.query as Record<string, unknown>) : undefined;

  trackEvent({
    event_type: TrackingEventType.PAGE_VIEW,
    module_id: resolveModuleId(to),
    page_path: pagePath,
    referrer_path: referrerPath,
    metadata: queryParams ? { query_params: queryParams } : undefined,
  });
};

router.afterEach(async (to, from) => {
  const { decrementAppBarLoadingCount } = useAppStore();
  document.title = typeof to.meta?.title === 'string' ? to.meta.title : ROUTER.APP_NAME;
  decrementAppBarLoadingCount();

  const shouldScrollToTopAfterRedirect =
    from.name !== to.name || (from.name === to.name && !!to.meta?.shouldScrollToTop);

  if (shouldScrollToTopAfterRedirect) window.scrollTo(0, 0);

  if (to.name && !to.meta?.isPublic && to.meta?.requiresAuth !== false && useAuthStore().getUser()) {
    await useModuleStore().fetchModules();
    trackPageView(to, from);
  }
});

export default router;
